import BezierEasing from 'bezier-easing'

export interface SplineBlob {
  id: string
  seed: number // For deterministic path generation

  // Position tracking
  positionProgress: number // 0-1 progress along current position segment
  positionSegment: number // Which position segment we're on

  // Speed tracking
  speedProgress: number // 0-1 progress along current speed segment
  speedSegment: number // Which speed segment we're on

  // Base properties
  baseSpeed: number // Base speed units per second
  speedVariance: number // 0.0-1.0, controls how chaotic vs gentle the speed splines are
  currentSpeed: number // Current calculated speed from speed spline
  size: number
  color: string
}

export interface SplinePath {
  segments: BezierSegment[]
  totalLength: number
}

export interface BezierSegment {
  startX: number
  startY: number
  cp1X: number
  cp1Y: number
  cp2X: number
  cp2Y: number
  endX: number
  endY: number
  length: number
}

// Speed spline segment with C1 continuity (position + tangent)
export interface SpeedSegment {
  startSpeed: number // Speed multiplier at segment start (0.5x - 2.0x)
  endSpeed: number // Speed multiplier at segment end
  startTangent: number // Rate of speed change at start
  endTangent: number // Rate of speed change at end
  length: number // Duration/length of this speed segment
  easing: BezierEasing.EasingFunction
}

export interface SpeedSpline {
  segments: SpeedSegment[]
  totalLength: number
}

// Combined spline system for a blob
export interface BlobSplineSystem {
  positionSpline: SplinePath
  speedSpline: SpeedSpline
}

export interface Point {
  x: number
  y: number
}

// Seeded random number generator for deterministic paths
class SeededRandom {
  private seed: number

  constructor(seed: number) {
    this.seed = seed
  }

  next(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280
    return this.seed / 233280
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min)
  }
}

// Generate a single continuous segment with C2 continuity
export function generateSingleContinuousSegment(
  seed: number,
  startPosition: Point,
  bounds: { width: number; height: number },
  previousSegment?: BezierSegment
): BezierSegment | null {
  const rng = new SeededRandom(seed)

  // Expand bounds to 120% for more controlled off-screen movement
  const chaosMultiplier = 1.2
  const expandedBounds = {
    width: bounds.width * chaosMultiplier,
    height: bounds.height * chaosMultiplier,
    offsetX: -bounds.width * (chaosMultiplier - 1) * 0.5,
    offsetY: -bounds.height * (chaosMultiplier - 1) * 0.5,
  }

  // Generate next point within expanded bounds
  const nextPoint: Point = {
    x: expandedBounds.offsetX + rng.next() * expandedBounds.width,
    y: expandedBounds.offsetY + rng.next() * expandedBounds.height,
  }

  // Calculate first control point with C2 continuity
  let cp1X: number, cp1Y: number

  if (previousSegment) {
    // For C2 continuity, mirror the previous segment's second control point
    const prevTangentX = previousSegment.endX - previousSegment.cp2X
    const prevTangentY = previousSegment.endY - previousSegment.cp2Y
    cp1X = startPosition.x + prevTangentX
    cp1Y = startPosition.y + prevTangentY
  } else {
    // No previous segment, generate first control point
    const cp1Distance = 150 + rng.next() * 500
    const cp1Angle = rng.next() * Math.PI * 2
    cp1X = startPosition.x + Math.cos(cp1Angle) * cp1Distance
    cp1Y = startPosition.y + Math.sin(cp1Angle) * cp1Distance
  }

  // Generate second control point with potential for loops
  const segmentDistance = Math.sqrt(
    (nextPoint.x - startPosition.x) ** 2 + (nextPoint.y - startPosition.y) ** 2
  )
  let cp2Distance = 150 + rng.next() * 500
  let cp2Angle = rng.next() * Math.PI * 2

  // 30% chance to create intentional loops
  if (rng.next() < 0.3) {
    const segmentAngle = Math.atan2(nextPoint.y - startPosition.y, nextPoint.x - startPosition.x)
    cp2Angle = segmentAngle + Math.PI / 2 + rng.range(-0.5, 0.5)
    cp2Distance = Math.max(cp2Distance, segmentDistance * rng.range(1.5, 2.5))
  }

  const cp2X = nextPoint.x + Math.cos(cp2Angle) * cp2Distance
  const cp2Y = nextPoint.y + Math.sin(cp2Angle) * cp2Distance

  const length = Math.sqrt(
    Math.pow(nextPoint.x - startPosition.x, 2) + Math.pow(nextPoint.y - startPosition.y, 2)
  )

  return {
    startX: startPosition.x,
    startY: startPosition.y,
    cp1X,
    cp1Y,
    cp2X,
    cp2Y,
    endX: nextPoint.x,
    endY: nextPoint.y,
    length,
  }
}

// Generate a continuous spline path starting from a specific position with C2 continuity
export function generateContinuousSplinePath(
  seed: number,
  startPosition: Point,
  bounds: { width: number; height: number },
  previousSegment?: BezierSegment
): SplinePath {
  const rng = new SeededRandom(seed)
  const segments: BezierSegment[] = []

  // Expand bounds to 120% for more controlled off-screen movement
  const chaosMultiplier = 1.2 // Keep within 120% of screen space
  const expandedBounds = {
    width: bounds.width * chaosMultiplier,
    height: bounds.height * chaosMultiplier,
    offsetX: -bounds.width * (chaosMultiplier - 1) * 0.5,
    offsetY: -bounds.height * (chaosMultiplier - 1) * 0.5,
  }

  let currentPoint = startPosition
  const numSegments = 3 + Math.floor(rng.next() * 3) // 3-5 segments

  // Calculate C2 continuous control points based on previous segment
  let firstCp1X: number, firstCp1Y: number

  if (previousSegment) {
    // For true C2 continuity, reuse the last control point from previous segment
    // The previous segment's end point is our start point
    // The previous segment's cp2 becomes our cp1 (mirrored for tangent continuity)
    const prevTangentX = previousSegment.endX - previousSegment.cp2X
    const prevTangentY = previousSegment.endY - previousSegment.cp2Y

    // First control point: mirror the previous segment's second control point
    firstCp1X = startPosition.x + prevTangentX
    firstCp1Y = startPosition.y + prevTangentY
  } else {
    // No previous segment, generate first control point
    const cp1Distance = 150 + rng.next() * 500
    const cp1Angle = rng.next() * Math.PI * 2
    firstCp1X = currentPoint.x + Math.cos(cp1Angle) * cp1Distance
    firstCp1Y = currentPoint.y + Math.sin(cp1Angle) * cp1Distance
  }

  for (let i = 0; i < numSegments; i++) {
    // Generate next point within expanded bounds
    const nextPoint: Point = {
      x: expandedBounds.offsetX + rng.next() * expandedBounds.width,
      y: expandedBounds.offsetY + rng.next() * expandedBounds.height,
    }

    // For C2 continuity, we only need to generate 2 new control points per segment
    let cp1X: number, cp1Y: number

    if (i === 0) {
      // First segment: use the pre-calculated first control point
      cp1X = firstCp1X
      cp1Y = firstCp1Y
    } else {
      // Subsequent segments: C2 continuity means cp1 mirrors previous cp2
      const prevSegment = segments[i - 1]
      const tangentX = prevSegment.endX - prevSegment.cp2X
      const tangentY = prevSegment.endY - prevSegment.cp2Y
      cp1X = currentPoint.x + tangentX
      cp1Y = currentPoint.y + tangentY
    }

    // Generate second control point with potential for loops
    const segmentDistance = Math.sqrt(
      (nextPoint.x - currentPoint.x) ** 2 + (nextPoint.y - currentPoint.y) ** 2
    )
    let cp2Distance = 150 + rng.next() * 500
    let cp2Angle = rng.next() * Math.PI * 2

    // 30% chance to create intentional loops by pointing control points inward
    if (rng.next() < 0.3) {
      const segmentAngle = Math.atan2(nextPoint.y - currentPoint.y, nextPoint.x - currentPoint.x)
      // Point control points perpendicular to segment to create loops
      cp2Angle = segmentAngle + Math.PI / 2 + rng.range(-0.5, 0.5)
      // Increase length for more dramatic loops, relative to segment distance
      cp2Distance = Math.max(cp2Distance, segmentDistance * rng.range(1.5, 2.5))
    }

    const cp2X = nextPoint.x + Math.cos(cp2Angle) * cp2Distance
    const cp2Y = nextPoint.y + Math.sin(cp2Angle) * cp2Distance

    const length = Math.sqrt(
      Math.pow(nextPoint.x - currentPoint.x, 2) + Math.pow(nextPoint.y - currentPoint.y, 2)
    )

    segments.push({
      startX: currentPoint.x,
      startY: currentPoint.y,
      cp1X,
      cp1Y,
      cp2X,
      cp2Y,
      endX: nextPoint.x,
      endY: nextPoint.y,
      length,
    })

    currentPoint = nextPoint
  }

  const totalLength = segments.reduce((sum, seg) => sum + seg.length, 0)
  return { segments, totalLength }
}

// Generate a deterministic spline path from a seed
export function generateSplinePath(
  seed: number,
  bounds: { width: number; height: number },
  segmentCount: number = 4
): SplinePath {
  const rng = new SeededRandom(seed)
  const segments: BezierSegment[] = []

  // Theoretical canvas is 30% larger for better visibility
  const theoreticalWidth = bounds.width * 1.3
  const theoreticalHeight = bounds.height * 1.3

  // Generate points within 120% of screen space
  const points: Point[] = []
  for (let i = 0; i <= segmentCount; i++) {
    // Use 120% constraint for better visibility
    const chaosMultiplier = 1.2 // Keep within 120% of screen space
    const wideX = theoreticalWidth * chaosMultiplier
    const wideY = theoreticalHeight * chaosMultiplier
    const wideOffsetX = bounds.width * chaosMultiplier * 0.5
    const wideOffsetY = bounds.height * chaosMultiplier * 0.5

    points.push({
      x: rng.range(-wideOffsetX, wideX - wideOffsetX),
      y: rng.range(-wideOffsetY, wideY - wideOffsetY),
    })
  }

  // Create cubic Bézier segments with smooth connections
  for (let i = 0; i < segmentCount; i++) {
    const start = points[i]
    const end = points[(i + 1) % points.length] // Loop back to start

    // Generate extremely chaotic control points that can create loops
    const baseLength = Math.sqrt((end.x - start.x) ** 2 + (end.y - start.y) ** 2)
    let tangentLength1 = rng.range(baseLength * 0.5, baseLength * 3.0) // Relative to segment distance
    let tangentLength2 = rng.range(baseLength * 0.5, baseLength * 3.0)

    // Allow full 360-degree freedom for wild paths
    let angle1 = rng.range(0, Math.PI * 2)
    let angle2 = rng.range(0, Math.PI * 2)

    // 30% chance to create intentional loops by pointing control points inward
    if (rng.next() < 0.3) {
      const segmentAngle = Math.atan2(end.y - start.y, end.x - start.x)
      // Point control points perpendicular to segment to create loops
      angle1 = segmentAngle + Math.PI / 2 + rng.range(-0.5, 0.5)
      angle2 = segmentAngle - Math.PI / 2 + rng.range(-0.5, 0.5)
      // Increase length for more dramatic loops
      tangentLength1 *= rng.range(1.5, 2.5)
      tangentLength2 *= rng.range(1.5, 2.5)
    }

    const cp1X = start.x + Math.cos(angle1) * tangentLength1
    const cp1Y = start.y + Math.sin(angle1) * tangentLength1
    const cp2X = end.x + Math.cos(angle2) * tangentLength2
    const cp2Y = end.y + Math.sin(angle2) * tangentLength2

    // Approximate segment length (good enough for relative timing)
    const length = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2))

    segments.push({
      startX: start.x,
      startY: start.y,
      cp1X,
      cp1Y,
      cp2X,
      cp2Y,
      endX: end.x,
      endY: end.y,
      length,
    })
  }

  const totalLength = segments.reduce((sum, seg) => sum + seg.length, 0)

  return { segments, totalLength }
}

// Evaluate position along a cubic Bézier curve
export function evaluateBezier(segment: BezierSegment, t: number): Point {
  // Use linear t - no easing for position! Speed control comes from speed spline only

  // Cubic Bézier formula: B(t) = (1-t)³P₀ + 3(1-t)²tP₁ + 3(1-t)t²P₂ + t³P₃
  const oneMinusT = 1 - t
  const oneMinusTSquared = oneMinusT * oneMinusT
  const oneMinusTCubed = oneMinusTSquared * oneMinusT
  const tSquared = t * t
  const tCubed = tSquared * t

  const x =
    oneMinusTCubed * segment.startX +
    3 * oneMinusTSquared * t * segment.cp1X +
    3 * oneMinusT * tSquared * segment.cp2X +
    tCubed * segment.endX

  const y =
    oneMinusTCubed * segment.startY +
    3 * oneMinusTSquared * t * segment.cp1Y +
    3 * oneMinusT * tSquared * segment.cp2Y +
    tCubed * segment.endY

  return { x, y }
}

// Generate a speed spline with C1 continuity (position + tangent)
export function generateSpeedSpline(
  seed: number,
  segmentCount: number = 4,
  previousSegment?: SpeedSegment,
  variance: number = 0.5 // 0.0 = gentle, 1.0 = chaotic
): SpeedSpline {
  const rng = new SeededRandom(seed + 1000) // Offset seed for speed variation
  const segments: SpeedSegment[] = []

  let currentSpeed = previousSegment ? previousSegment.endSpeed : rng.range(0.7, 1.3)
  let currentTangent = previousSegment ? previousSegment.endTangent : rng.range(-0.8, 0.8)

  for (let i = 0; i < segmentCount; i++) {
    // Use variance to bias pattern selection toward gentle (low variance) or chaotic (high variance)
    const patternRoll = rng.next()

    // Calculate pattern thresholds based on variance
    // Low variance: more gentle patterns (cruise, gentle ramp, soft oscillation)
    // High variance: more chaotic patterns (burst, spike, pause, extreme oscillation)
    const gentleChance = 0.6 * (1 - variance) // 0.6 to 0.0 as variance increases
    const burstChance = gentleChance + 0.2 * variance // Scaled with variance
    const pauseChance = burstChance + 0.15 * variance
    const rampChance = pauseChance + 0.2 // Always moderate chance
    const oscillationChance = rampChance + (0.1 + 0.1 * variance)
    const spikeChance = oscillationChance + 0.15 * variance

    let nextSpeed: number
    let endTangent: number
    let segmentLength: number
    let easing: BezierEasing.EasingFunction

    if (patternRoll < gentleChance) {
      // CRUISE pattern: steady moderate speed (more likely with low variance)
      const speedRange = 0.1 + 0.2 * variance // 0.1-0.3 range based on variance
      nextSpeed = rng.range(1.0 - speedRange, 1.0 + speedRange)
      endTangent = rng.range(-0.2 * (1 + variance), 0.2 * (1 + variance)) // Gentle transitions, slightly more with variance
      segmentLength = rng.range(3.0, 6.0)
      easing = BezierEasing(
        rng.range(0.4, 0.6),
        rng.range(0.4, 0.6),
        rng.range(0.4, 0.6),
        rng.range(0.4, 0.6)
      )
    } else if (patternRoll < burstChance) {
      // BURST pattern: sudden acceleration then deceleration (more intense with high variance)
      const intensityFactor = 0.5 + 0.5 * variance // Scale intensity with variance
      nextSpeed = rng.range(1.2 + 0.5 * intensityFactor, 1.5 + 0.5 * intensityFactor)
      endTangent = rng.range(-0.4 * (1 + variance), -0.2 * (1 + variance))
      segmentLength = rng.range(1.0, 3.0)
      easing = BezierEasing(0.8 + 0.1 * variance, 0.1, 0.1, 0.8 + 0.1 * variance)
    } else if (patternRoll < pauseChance) {
      // PAUSE pattern: slow down (more dramatic with high variance)
      const slowFactor = 0.3 * (1 - variance) // More dramatic slowdown with high variance
      nextSpeed = rng.range(0.5 + slowFactor, 0.7 + slowFactor)
      endTangent = rng.range(-0.1 * (1 + variance), 0.1 * (1 + variance))
      segmentLength = rng.range(3.0, 6.0)
      easing = BezierEasing(0.2, 0.8, 0.8, 0.2)
    } else if (patternRoll < rampChance) {
      // RAMP pattern: steady acceleration or deceleration (intensity scales with variance)
      const isAccelerating = rng.next() < 0.5
      const rampIntensity = 0.5 + 0.5 * variance
      nextSpeed = isAccelerating
        ? rng.range(1.2 + 0.3 * rampIntensity, 1.5 + 0.5 * rampIntensity)
        : rng.range(0.5 + 0.3 * (1 - rampIntensity), 0.8)
      endTangent = isAccelerating
        ? rng.range(0.3 * rampIntensity, 0.6 * rampIntensity)
        : rng.range(-0.6 * rampIntensity, -0.3 * rampIntensity)
      segmentLength = rng.range(4.0, 7.0)
      easing = isAccelerating ? BezierEasing(0.1, 0.7, 0.3, 1.0) : BezierEasing(0.7, 0.0, 1.0, 0.3)
    } else if (patternRoll < oscillationChance) {
      // OSCILLATION pattern: speed up and slow down (more extreme with high variance)
      const oscillationRange = 0.2 + 0.3 * variance
      nextSpeed = rng.range(1.0 - oscillationRange, 1.0 + oscillationRange)
      endTangent = rng.range(-0.3 * (1 + variance), 0.3 * (1 + variance))
      segmentLength = rng.range(2.5, 5.0)
      easing = BezierEasing(
        rng.range(0.5 + 0.2 * variance, 0.8 + 0.1 * variance),
        rng.range(0.0, 0.2 + 0.1 * variance),
        rng.range(0.6 + 0.1 * variance, 0.9 + 0.1 * variance),
        rng.range(0.1, 0.3 + 0.1 * variance)
      )
    } else if (patternRoll < spikeChance) {
      // SPIKE pattern: brief intense speed change (more extreme with high variance)
      const isFastSpike = rng.next() < 0.6
      const spikeIntensity = 0.5 + 0.5 * variance
      nextSpeed = isFastSpike
        ? rng.range(1.5 + 0.3 * spikeIntensity, 1.8 + 0.2 * spikeIntensity)
        : rng.range(0.5, 0.6 - 0.1 * spikeIntensity)
      endTangent = isFastSpike
        ? rng.range(0.4 * spikeIntensity, 0.8 * spikeIntensity)
        : rng.range(-0.6 * spikeIntensity, -0.3 * spikeIntensity)
      segmentLength = rng.range(0.8, 2.0)
      easing = BezierEasing(
        rng.range(0.7 + 0.2 * variance, 1.0),
        rng.range(0.0, 0.1 + 0.1 * variance),
        rng.range(0.7 + 0.2 * variance, 1.0),
        rng.range(0.0, 0.1 + 0.1 * variance)
      )
    } else {
      // GENTLE pattern: very smooth moderate changes (fallback for low variance)
      nextSpeed = rng.range(0.9, 1.1)
      endTangent = rng.range(-0.1, 0.1) // Very gentle transitions
      segmentLength = rng.range(4.0, 7.0) // Longer segments
      easing = BezierEasing(0.4, 0.6, 0.4, 0.6) // Very smooth curve
    }

    // For C1 continuity: start tangent = previous end tangent
    const startTangent = currentTangent

    segments.push({
      startSpeed: currentSpeed,
      endSpeed: nextSpeed,
      startTangent,
      endTangent,
      length: segmentLength,
      easing,
    })

    // Prepare for next segment
    currentSpeed = nextSpeed
    currentTangent = endTangent
  }

  const totalLength = segments.reduce((sum, seg) => sum + seg.length, 0)
  return { segments, totalLength }
}

// Evaluate speed multiplier along a speed spline segment using cubic hermite interpolation
export function evaluateSpeedSegment(segment: SpeedSegment, t: number): number {
  // Apply easing to t
  const easedT = segment.easing(t)

  // Cubic Hermite interpolation for C1 continuity
  // p(t) = (2t³ - 3t² + 1) * p0 + (t³ - 2t² + t) * m0 + (-2t³ + 3t²) * p1 + (t³ - t²) * m1
  const t2 = easedT * easedT
  const t3 = t2 * easedT

  const h00 = 2 * t3 - 3 * t2 + 1
  const h10 = t3 - 2 * t2 + easedT
  const h01 = -2 * t3 + 3 * t2
  const h11 = t3 - t2

  return (
    h00 * segment.startSpeed +
    h10 * segment.startTangent +
    h01 * segment.endSpeed +
    h11 * segment.endTangent
  )
}

// Generate combined spline system for a blob
export function generateBlobSplineSystem(
  seed: number,
  bounds: { width: number; height: number },
  positionSegmentCount: number = 4,
  speedSegmentCount: number = 3,
  speedVariance: number = 0.5
): BlobSplineSystem {
  const positionSpline = generateSplinePath(seed, bounds, positionSegmentCount)
  const speedSpline = generateSpeedSpline(seed, speedSegmentCount, undefined, speedVariance)

  return {
    positionSpline,
    speedSpline,
  }
}

// Get current position of a blob along its spline path
export function getBlobPosition(blob: SplineBlob, splinePath: SplinePath): Point {
  if (splinePath.segments.length === 0) {
    return { x: 0, y: 0 }
  }

  const segment = splinePath.segments[blob.positionSegment % splinePath.segments.length]
  return evaluateBezier(segment, blob.positionProgress)
}

// Get current speed multiplier from speed spline
export function getBlobSpeed(blob: SplineBlob, speedSpline: SpeedSpline): number {
  if (speedSpline.segments.length === 0) {
    return 1.0
  }

  const segment = speedSpline.segments[blob.speedSegment % speedSpline.segments.length]
  return evaluateSpeedSegment(segment, blob.speedProgress)
}

// Update blob progress along dual spline system
export function updateBlobProgress(
  blob: SplineBlob,
  splineSystem: BlobSplineSystem,
  deltaTime: number,
  globalSpeedMultiplier: number = 1
): SplineBlob {
  const { positionSpline, speedSpline } = splineSystem

  // Get current speed multiplier from speed spline
  const speedMultiplier = getBlobSpeed(blob, speedSpline)
  const totalSpeed = blob.baseSpeed * speedMultiplier * globalSpeedMultiplier

  // Update position progress
  const positionSegment =
    positionSpline.segments[blob.positionSegment % positionSpline.segments.length]
  const positionProgressDelta = (totalSpeed * deltaTime) / (positionSegment.length * 1000)

  let newPositionProgress = blob.positionProgress + positionProgressDelta
  let newPositionSegment = blob.positionSegment

  // Handle position segment transitions
  if (newPositionProgress >= 1.0) {
    newPositionProgress = 0.0
    newPositionSegment = (newPositionSegment + 1) % positionSpline.segments.length
  }

  // Update speed progress (independent of position progress)
  const speedSegment = speedSpline.segments[blob.speedSegment % speedSpline.segments.length]
  const speedProgressDelta = (deltaTime * 2) / (speedSegment.length * 1000) // Speed spline progresses with time, not distance

  let newSpeedProgress = blob.speedProgress + speedProgressDelta
  let newSpeedSegment = blob.speedSegment

  // Handle speed segment transitions
  if (newSpeedProgress >= 1.0) {
    newSpeedProgress = 0.0
    newSpeedSegment = (newSpeedSegment + 1) % speedSpline.segments.length
  }

  return {
    ...blob,
    positionProgress: newPositionProgress,
    positionSegment: newPositionSegment,
    speedProgress: newSpeedProgress,
    speedSegment: newSpeedSegment,
    currentSpeed: totalSpeed,
  }
}

// Create initial spline blob with deterministic or random properties
export function createSplineBlob(
  id: string,
  _bounds: { width: number; height: number }, // Reserved for future position calculations
  colorOptions: string[],
  masterSeed?: number,
  elapsedTime?: number
): SplineBlob {
  // Use master seed + blob index for deterministic generation
  const blobIndex = parseInt(id.split('-')[1] || '0')
  const seed = masterSeed ? masterSeed + blobIndex * 1000 : Math.floor(Math.random() * 1000000)
  const rng = new SeededRandom(seed)

  // If elapsed time provided, calculate current progress
  let positionProgress = rng.next() // Default random start
  let positionSegment = Math.floor(rng.range(0, 4))
  let speedProgress = rng.next() // Independent random start for speed
  let speedSegment = Math.floor(rng.range(0, 3))

  if (elapsedTime && masterSeed) {
    // Calculate deterministic progress based on elapsed time
    const speed = rng.range(0.5, 2.0)
    const totalProgress = (speed * elapsedTime) / 10000 // Convert to progress units
    positionSegment = Math.floor(totalProgress) % 4
    positionProgress = totalProgress % 1

    // Speed spline progresses independently with time
    const timeProgress = elapsedTime / 5000 // Different timing for speed
    speedSegment = Math.floor(timeProgress) % 3
    speedProgress = timeProgress % 1
  }

  const baseSpeed = rng.range(0.5, 2.0)
  const speedVariance = rng.range(0.2, 0.8) // 0.2-0.8 variance range for variety

  return {
    id,
    seed,
    positionProgress,
    positionSegment,
    speedProgress,
    speedSegment,
    baseSpeed,
    speedVariance,
    currentSpeed: baseSpeed, // Initialize with base speed
    size: rng.range(280, 320),
    color: colorOptions[Math.floor(rng.range(0, colorOptions.length))],
  }
}

// Infinite spline system for continuous extension
export class InfiniteSpline {
  private segments: BezierSegment[] = []
  private segmentStartIndices: number[] = [] // Maps logical segment index to array index
  private windowStart: number = 0 // First segment index in current window
  private windowSize: number = 12 // Increased from 6 to reduce generation frequency
  private seed: number
  private bounds: { width: number; height: number }
  private _rng: SeededRandom // Reserved for future random operations
  private batchSize: number = 3 // Generate multiple segments at once

  constructor(
    seed: number,
    bounds: { width: number; height: number },
    initialSegmentCount: number = 8 // Increased from 4 for better startup performance
  ) {
    this.seed = seed
    this.bounds = bounds
    this._rng = new SeededRandom(seed)

    // Generate initial segments
    this.generateInitialSegments(initialSegmentCount)
  }

  private generateInitialSegments(count: number) {
    const initialPath = generateSplinePath(this.seed, this.bounds, count)
    this.segments = [...initialPath.segments]

    // Initialize segment mapping
    for (let i = 0; i < count; i++) {
      this.segmentStartIndices[i] = i
    }
  }

  // Extend spline forward with C2 continuity (batch generation for better performance)
  private extendForward(count: number = 1) {
    for (let i = 0; i < count; i++) {
      const lastSegment = this.segments[this.segments.length - 1]
      const segmentIndex = this.windowStart + this.segments.length

      // Generate next segment with C2 continuity
      // Request only 1 segment to ensure proper C2 continuity in batches
      const nextSegment = generateSingleContinuousSegment(
        this.seed + segmentIndex,
        { x: lastSegment.endX, y: lastSegment.endY },
        this.bounds,
        lastSegment
      )

      if (nextSegment) {
        this.segments.push(nextSegment)
        this.segmentStartIndices[segmentIndex] = this.segments.length - 1
      }
    }
  }

  // Remove old segments to prevent memory growth
  private trimBehind() {
    if (this.segments.length > this.windowSize) {
      const removeCount = this.segments.length - this.windowSize
      this.segments.splice(0, removeCount)
      this.windowStart += removeCount

      // Update segment mapping
      const newMapping: number[] = []
      for (let i = this.windowStart; i < this.windowStart + this.segments.length; i++) {
        const oldIndex = this.segmentStartIndices[i]
        if (oldIndex !== undefined) {
          newMapping[i] = oldIndex - removeCount
        }
      }
      this.segmentStartIndices = newMapping
    }
  }

  // Get segment for blob position, extending if necessary
  getSegment(segmentIndex: number): BezierSegment {
    // Batch extend if we need segments beyond current window
    const segmentsNeeded = segmentIndex - (this.windowStart + this.segments.length) + 1
    if (segmentsNeeded > 0) {
      // Generate in batches to reduce function call overhead
      const batchCount = Math.max(this.batchSize, segmentsNeeded)
      this.extendForward(batchCount)
    }

    // Trim old segments if window is too large
    this.trimBehind()

    const arrayIndex = segmentIndex - this.windowStart
    return this.segments[Math.max(0, Math.min(arrayIndex, this.segments.length - 1))]
  }

  // Get current window of segments for debugging
  getCurrentSegments(): BezierSegment[] {
    return [...this.segments]
  }

  // Get current window start offset for debugging
  getWindowStart(): number {
    return this.windowStart
  }

  // Get debug info including window offset
  getDebugInfo(): { segments: BezierSegment[]; windowStart: number; totalLength: number } {
    return {
      segments: [...this.segments],
      windowStart: this.windowStart,
      totalLength: this.segments.reduce((sum, seg) => sum + seg.length, 0),
    }
  }
}

// Infinite speed spline system
export class InfiniteSpeedSpline {
  private segments: SpeedSegment[] = []
  private windowStart: number = 0
  private windowSize: number = 8 // Increased from 4 to reduce generation frequency
  private seed: number
  private variance: number
  private _rng: SeededRandom // Reserved for future random operations
  private batchSize: number = 2 // Generate multiple segments at once

  constructor(seed: number, initialSegmentCount: number = 6, variance: number = 0.5) {
    this.seed = seed
    this.variance = variance
    this._rng = new SeededRandom(seed + 1000)

    // Generate initial segments (increased from 3 to 6)
    this.generateInitialSegments(initialSegmentCount)
  }

  private generateInitialSegments(count: number) {
    const initialSpline = generateSpeedSpline(this.seed, count, undefined, this.variance)
    this.segments = [...initialSpline.segments]
  }

  // Extend speed spline forward with C1 continuity (batch generation for better performance)
  private extendForward(count: number = 1) {
    let lastSegment = this.segments[this.segments.length - 1]

    for (let i = 0; i < count; i++) {
      const segmentIndex = this.windowStart + this.segments.length

      // Generate next segment with C1 continuity and variance
      const nextSpline = generateSpeedSpline(
        this.seed + segmentIndex,
        1,
        lastSegment,
        this.variance
      )

      if (nextSpline.segments.length > 0) {
        this.segments.push(nextSpline.segments[0])
        lastSegment = nextSpline.segments[0]
      }
    }
  }

  private trimBehind() {
    if (this.segments.length > this.windowSize) {
      const removeCount = this.segments.length - this.windowSize
      this.segments.splice(0, removeCount)
      this.windowStart += removeCount
    }
  }

  private slideWindowBackward(slideAmount: number) {
    // Generate segments backward to cover the requested range
    for (let i = 0; i < slideAmount; i++) {
      const targetIndex = this.windowStart - 1 - i
      const spline = generateSpeedSpline(this.seed + targetIndex, 1, undefined, this.variance)
      // Add new segments at the beginning of the array
      this.segments.unshift(spline.segments[0])
    }

    // Update window start position
    this.windowStart -= slideAmount

    // Trim from the end if we exceed window size
    if (this.segments.length > this.windowSize) {
      const removeCount = this.segments.length - this.windowSize
      this.segments.splice(-removeCount, removeCount)
    }
  }

  getSegment(segmentIndex: number): SpeedSegment {
    // Handle segments that are behind the current window by sliding the window backward
    if (segmentIndex < this.windowStart) {
      const slideAmount = this.windowStart - segmentIndex
      this.slideWindowBackward(slideAmount)
    }

    // Batch extend if we need segments beyond current window
    const segmentsNeeded = segmentIndex - (this.windowStart + this.segments.length) + 1
    if (segmentsNeeded > 0) {
      // Generate in batches to reduce function call overhead
      const batchCount = Math.max(this.batchSize, segmentsNeeded)

      // Warn if we're jumping unexpectedly far (suggests a bug in progress tracking)
      if (batchCount > 20 && process.env.NODE_ENV === 'development') {
        console.warn(
          `[InfiniteSpeedSpline] Large segment jump detected: extending ${batchCount} segments for index ${segmentIndex}, windowStart ${this.windowStart}`
        )
      }

      // Always extend what we need - no arbitrary cap to prevent animation freezes
      this.extendForward(batchCount)
    }

    // Calculate the array index
    const arrayIndex = segmentIndex - this.windowStart

    // Validate the segment is accessible (should not happen now with backward sliding)
    if (arrayIndex < 0) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `[InfiniteSpeedSpline] Segment ${segmentIndex} is still behind window (${this.windowStart}) after sliding, using fallback`
        )
      }
      return this.createFallbackSegment()
    }

    if (arrayIndex >= this.segments.length) {
      if (process.env.NODE_ENV === 'development') {
        console.error(
          `[InfiniteSpeedSpline] Segment ${segmentIndex} still out of bounds after extension`
        )
      }
      return this.createFallbackSegment()
    }

    // Only trim after we've successfully accessed the segment we need
    if (this.segments.length > this.windowSize + 2) {
      // Keep extra buffer
      this.trimBehind()
      // Recalculate array index after trimming
      const newArrayIndex = segmentIndex - this.windowStart
      if (newArrayIndex >= 0 && newArrayIndex < this.segments.length) {
        return this.segments[newArrayIndex]
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[InfiniteSpeedSpline] Segment lost after trimming, using fallback`)
        }
        return this.createFallbackSegment()
      }
    }

    // Return the requested segment
    return this.segments[arrayIndex]
  }

  // Create a fallback segment when the system is in an invalid state
  private createFallbackSegment(): SpeedSegment {
    return {
      startSpeed: 1.0,
      endSpeed: 1.0,
      startTangent: 0.0,
      endTangent: 0.0,
      length: 5.0,
      easing: (t: number) => t, // Linear easing fallback
    }
  }

  // Get debug info including window offset
  getDebugInfo(): { segments: SpeedSegment[]; windowStart: number; totalLength: number } {
    return {
      segments: [...this.segments],
      windowStart: this.windowStart,
      totalLength: this.segments.reduce((sum, seg) => sum + seg.length, 0),
    }
  }
}

// Combined infinite spline system for a blob
export interface InfiniteBlobSplineSystem {
  positionSpline: InfiniteSpline
  speedSpline: InfiniteSpeedSpline
}

// Create infinite spline system for a blob
export function createInfiniteBlobSplineSystem(
  seed: number,
  bounds: { width: number; height: number },
  speedVariance: number = 0.5
): InfiniteBlobSplineSystem {
  return {
    positionSpline: new InfiniteSpline(seed, bounds), // Uses default 8 initial segments
    speedSpline: new InfiniteSpeedSpline(seed, 6, speedVariance), // Increased from 3 to 6
  }
}

// Update blob progress with infinite splines
export function updateBlobProgressInfinite(
  blob: SplineBlob,
  splineSystem: InfiniteBlobSplineSystem,
  deltaTime: number,
  globalSpeedMultiplier: number = 1
): SplineBlob {
  const { positionSpline, speedSpline } = splineSystem

  // Get current speed multiplier from infinite speed spline
  const speedSegment = speedSpline.getSegment(blob.speedSegment)
  const speedMultiplier = evaluateSpeedSegment(speedSegment, blob.speedProgress)
  const totalSpeed = blob.baseSpeed * speedMultiplier * globalSpeedMultiplier

  // Update position progress with infinite position spline
  const positionSegment = positionSpline.getSegment(blob.positionSegment)
  const positionProgressDelta = (totalSpeed * deltaTime) / (positionSegment.length * 1000)

  let newPositionProgress = blob.positionProgress + positionProgressDelta
  let newPositionSegment = blob.positionSegment

  // Handle position segment transitions (infinite)
  if (newPositionProgress >= 1.0) {
    newPositionProgress = 0.0
    newPositionSegment = newPositionSegment + 1 // No modulo - infinite extension
  }

  // Update speed progress (infinite) - speedSegment.length is in seconds, deltaTime is in ms
  // Speed segments should cycle faster to show more dynamic speed variations
  const speedProgressDelta = (deltaTime * 2) / (speedSegment.length * 1000)

  let newSpeedProgress = blob.speedProgress + speedProgressDelta
  let newSpeedSegment = blob.speedSegment

  // Handle speed segment transitions (infinite)
  if (newSpeedProgress >= 1.0) {
    newSpeedProgress = 0.0
    newSpeedSegment = newSpeedSegment + 1 // No modulo - infinite extension
  }

  return {
    ...blob,
    positionProgress: newPositionProgress,
    positionSegment: newPositionSegment,
    speedProgress: newSpeedProgress,
    speedSegment: newSpeedSegment,
    currentSpeed: totalSpeed,
  }
}

// Get position from infinite spline system
export function getBlobPositionInfinite(
  blob: SplineBlob,
  splineSystem: InfiniteBlobSplineSystem
): Point {
  const segment = splineSystem.positionSpline.getSegment(blob.positionSegment)
  return evaluateBezier(segment, blob.positionProgress)
}

// Get speed from infinite spline system
export function getBlobSpeedInfinite(
  blob: SplineBlob,
  splineSystem: InfiniteBlobSplineSystem
): number {
  const segment = splineSystem.speedSpline.getSegment(blob.speedSegment)
  return evaluateSpeedSegment(segment, blob.speedProgress)
}

// Calculate elapsed time-based progress for deterministic startup
export function calculateProgressFromTime(
  elapsedMs: number,
  _seed: number, // Reserved for future deterministic calculations
  speed: number
): {
  positionProgress: number
  positionSegment: number
  speedProgress: number
  speedSegment: number
} {
  const totalProgress = (speed * elapsedMs) / 10000
  const timeProgress = elapsedMs / 5000

  return {
    positionSegment: Math.floor(totalProgress) % 4,
    positionProgress: totalProgress % 1,
    speedSegment: Math.floor(timeProgress) % 3,
    speedProgress: timeProgress % 1,
  }
}
